import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  Briefcase,
  Send,
  Heart,
  Settings,
  Globe,
  Shield,
  type LucideIcon
} from 'lucide-react';
import { theme } from '../../../styles/theme';
import dashboardApi, { NonAggregatableMetricsResponse } from '../../../api/dashboardApi';

interface NonAggregatableMetricsProps {
  darkMode: boolean;
  countryId?: number;
}

const NonAggregatableMetrics: React.FC<NonAggregatableMetricsProps> = ({ darkMode, countryId = 1 }) => {
  const [data, setData] = useState<NonAggregatableMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());

  useEffect(() => {
    console.log('NonAggregatableMetrics component mounted with countryId:', countryId);
    fetchNonAggregatableMetrics();
  }, [countryId]);

  const fetchNonAggregatableMetrics = async () => {
    try {
      console.log('Fetching non-aggregatable metrics for countryId:', countryId);
      setLoading(true);
      setError(null);
      const response = await dashboardApi.getNonAggregatableMetrics(countryId);
      console.log('Non-aggregatable metrics response:', response);
      console.log('Cluster codes received:', response.clusters?.map(c => c.code));
      setData(response);
    } catch (err) {
      setError('Failed to load non-aggregatable metrics');
      console.error('Error fetching non-aggregatable metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCluster = (clusterCode: string) => {
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clusterCode)) {
        newSet.delete(clusterCode);
      } else {
        newSet.add(clusterCode);
      }
      return newSet;
    });
  };

  const getChangeIcon = (change: string) => {
    if (change.includes('+')) {
      return <TrendingUp className="w-4 h-4" style={{ color: theme.colors.status.success }} />;
    } else if (change.includes('-')) {
      return <TrendingDown className="w-4 h-4" style={{ color: theme.colors.status.error }} />;
    }
    return <Minus className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />;
  };

  const formatUnitLabel = (unit: string): string => {
    const unitLabels: Record<string, string> = {
      'expense': 'Currency',
      'income': 'Currency',
      'people_count': 'People Count',
      'time_duration': 'Days',
      'percentage': 'Percentage',
      'rate': 'Rate',
      'ratio': 'Ratio',
      'score': 'Score',
      'index': 'Index',
    };
    return unitLabels[unit] || unit.replace('_', ' ');
  };

  const getClusterIcon = (clusterCode: string): LucideIcon => {
    // Map cluster codes to Lucide React icons - matching the impact cluster cards exactly
    // Database uses C1-C7 codes, not CR/LI/etc
    const iconMap: Record<string, LucideIcon> = {
      'C1': DollarSign,     // Cost Reduction
      'C2': Briefcase,      // Labour Integration
      'C3': Send,           // Remittances
      'C4': Heart,          // SROI
      'C5': Settings,       // Systemic Friction
      'C6': Globe,          // Climate Externalities
      'C7': Shield,         // Risk & Volatility Mitigation
      // Legacy mappings in case they still come through
      'CR': DollarSign,     // Cost Reduction (C1)
      'LI': Briefcase,      // Labour Integration (C2)
      'REM': Send,          // Remittances (C3)
      'SROI': Heart,        // SROI (C4)
      'SF': Settings,       // Systemic Friction (C5)
      'CE': Globe,          // Climate Externalities (C6)
      'RVM': Shield,        // Risk & Volatility Mitigation (C7)
    };
    return iconMap[clusterCode] || Settings;
  };

  if (loading) {
    return (
      <div className="rounded-lg p-6" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg p-6" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}>
        <div className="text-center py-8">
          <p style={{ color: theme.colors.status.error }}>{error}</p>
          <button 
            onClick={fetchNonAggregatableMetrics}
            className="mt-4 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: theme.colors.primary.main,
              color: 'white',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.clusters.length === 0) {
    return (
      <div className="rounded-lg p-6" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}>
        <p style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
          No non-aggregatable metrics available
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg" style={{
      backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
      border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
    }}>
      {/* Header */}
      <div className="p-6 border-b" style={{
        borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
      }}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2" style={{
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
            }}>
              <span className="text-2xl">📊</span>
              Non-Aggregatable Metrics by Cluster
            </h2>
            <p className="text-sm mt-1" style={{
              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
            }}>
              These metrics cannot be summed but provide important context. They include ratios, percentages, rates, and scores.
            </p>
          </div>
          <div className="px-3 py-1 rounded-full text-sm font-medium" style={{
            backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
            color: theme.colors.primary.main,
          }}>
            {data.totalCount} total
          </div>
        </div>
      </div>

      {/* Clusters */}
      <div className="divide-y" style={{
        borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
      }}>
        {data.clusters.map((cluster) => (
          <div key={cluster.code}>
            {/* Cluster Header */}
            <div
              className="px-6 py-4 cursor-pointer hover:bg-opacity-5 hover:bg-black transition-colors flex items-center justify-between"
              onClick={() => toggleCluster(cluster.code)}
            >
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = getClusterIcon(cluster.code);
                  return (
                    <Icon
                      className="w-6 h-6"
                      style={{
                        color: theme.colors.primary.main
                      }}
                    />
                  );
                })()}
                <div>
                  <h3 className="font-medium" style={{
                    color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                  }}>
                    {cluster.name}
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-sm" style={{
                  backgroundColor: theme.colors.primary.light + '20',
                  color: theme.colors.primary.main,
                }}>
                  {cluster.metrics.length} {cluster.metrics.length === 1 ? 'metric' : 'metrics'}
                </span>
                {expandedClusters.has(cluster.code) ? (
                  <ChevronDown className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
                ) : (
                  <ChevronRight className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
                )}
              </div>
            </div>

            {/* Cluster Metrics */}
            {expandedClusters.has(cluster.code) && (
              <div className="px-6 pb-4">
                <div className="ml-11 space-y-3">
                  {cluster.metrics.map((metric) => (
                    <div
                      key={metric.id}
                      className="p-4 rounded-lg border transition-all hover:shadow-sm"
                      style={{
                        backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                        borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium" style={{
                              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                            }}>
                              {metric.name}
                            </h4>
                            {metric.unit && (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{
                                backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
                                color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                                border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                              }}>
                                {formatUnitLabel(metric.unit)}
                              </span>
                            )}
                          </div>
                          {metric.definition && (
                            <p className="text-sm mb-2" style={{
                              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                            }}>
                              {metric.definition}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-2xl font-semibold mb-1" style={{
                            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                          }}>
                            {metric.value}
                          </div>
                          <div className="flex items-center gap-1 justify-end">
                            {getChangeIcon(metric.change)}
                            <span className="text-sm" style={{
                              color: metric.change.includes('+') ? theme.colors.status.success :
                                     metric.change.includes('-') ? theme.colors.status.error :
                                     theme.colors.text.secondary,
                            }}>
                              {metric.change}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NonAggregatableMetrics;